import { createSelector } from "reselect";
import produce from "immer";

import { getVendors } from "src/store/vendor/selector";
import { getMileageInTimeZone } from "src/pages/TransactionDetails/Expenses/mileage";
import { calendarYmdInTimeZone } from "src/utils/timezone";
import { effectiveUserTimeZoneSelector } from "../system/selector";
import type {
  Transaction,
  TransactionWithDetails,
} from "src/interfaces/transaction.interface";
import { filter } from "src/utils/filters";
import type { AppState } from "..";
import { getAllSales, salesAsTransactionsSelector } from "../sale/selector";
import {
  generalLedgerAccountsSelector,
  generalLedgerAccountSelector,
} from "../glAccounts/selector";
import {
  getInventory,
  inventoryAsTransactionsSelector,
} from "../inventory/selector";
import type { Sale } from "src/interfaces/sale.interface";
import type { Inventory } from "src/interfaces/inventory.interface";
import type { MappedTransaction } from "src/interfaces/transaction.interface";
import { toDateString } from "src/utils/date";

import { bankAccountsSelector } from "../bankAccount/selector/core";

const getTransactionState = (state: AppState) => state.transaction;
export const oldestTransactionYearSelector = createSelector(
  [getTransactionState],
  (state) => state.oldestYear
);
const transactionItemsSelector = createSelector(
  [getTransactionState],
  (transactions) => {
    const list = Object.values(transactions.itemsById);
    list.sort((a, b) => {
      const ad = a.date ? new Date(a.date as Date).getTime() : 0;
      const bd = b.date ? new Date(b.date as Date).getTime() : 0;
      return bd - ad;
    });
    return list;
  }
);

export const transactionsSelector = createSelector(
  [
    transactionItemsSelector,
    getVendors,
    generalLedgerAccountsSelector,
    bankAccountsSelector,
  ],
  (transactions, vendors, accounts, bankAccounts) =>
    transactions.map((transaction) =>
      produce(transaction as TransactionWithDetails, (draft) => {
        if (draft.vendor) {
          const vendor = vendors.find((v) => v.id === draft.vendor);
          if (vendor) {
            draft.vendorName = vendor.name;
            draft.vendorDoc = vendor;
          }
        }

        if (draft.gl_account_id) {
          const account = accounts.find((a) => a.id === draft.gl_account_id);
          if (account) {
            draft.account = account.name;
            draft.accountDoc = account;
          }
        } else {
          const account = accounts.find((a) => a.name === draft.account);
          if (account) {
            draft.gl_account_id = account.id;
            draft.accountDoc = account;
          }
        }

        if (draft.bank_account_id) {
          const bankAccount = bankAccounts.find(
            (ba) => ba.id === draft.bank_account_id
          );
          if (bankAccount) {
            draft.integration_name = bankAccount.name;
          } else {
            delete draft.bank_account_id;
            delete draft.integration_name;
          }
        } else if (draft.integration_name) {
          const bankAccount = bankAccounts.find(
            (ba) => ba.name === draft.integration_name
          );
          if (bankAccount) draft.bank_account_id = bankAccount.id;
        }
      })
    ) as TransactionWithDetails[]
);

export const getTransactions = createSelector(
  [getTransactionState, transactionsSelector],
  (transactions, items) => {
    const { itemsById: _itemsById, ...rest } = transactions;
    const reviewedTransactions = items.filter(
      (item) => item.reviewed || item.reviewed == null
    );
    const unreviewedTransactions = items.filter(
      (item) => !item.reviewed && item.reviewed != null
    );
    return {
      ...rest,
      items,
      reviewedTransactions,
      unreviewedTransactions,
    };
  }
);

export const getEarliestTransactionYear = createSelector(
  transactionsSelector,
  (transactions) => {
    const minDate = transactions.reduce((result, item) => {
      if (!item.date) return result;
      const date = new Date(item.date);
      if (isNaN(date.valueOf())) return result;
      if (date < result) return date;
      return result;
    }, new Date());
    return +minDate.getFullYear();
  }
);

export const mileageForYearSelector = createSelector(
  [transactionsSelector, (_, year) => year, effectiveUserTimeZoneSelector],
  (items, y, tz) => {
    const yearNum = parseInt(`${y}`, 10);
    const yStart = `${yearNum}-01-01`;
    const yEnd = `${yearNum}-12-31`;

    return items.reduce((mileage, transaction) => {
      const d = transaction.date;
      if (!d) return mileage;
      const ymd = calendarYmdInTimeZone(d, tz);
      if (ymd < yStart || ymd > yEnd) return mileage;
      if (
        transaction.account === "Sourcing Mileage/Transportation" ||
        transaction.account === "Non-Sourcing Mileage/Transportation"
      ) {
        return mileage + getMileageInTimeZone(d, transaction.amount, tz);
      }
      return mileage;
    }, 0);
  }
);

export const haveTransactionsLoadedSelector = createSelector(
  [getTransactionState],
  (transactionState) => transactionState.loaded || false
);

const expensesOnSalesSelector = createSelector(
  [getAllSales, bankAccountsSelector],
  (sales, bankAccounts) => {
    const expenses: any[] = [];

    for (const sale of sales) {
      const expense: any = {
        description: sale.item_title || "",
        date:
          sale.is_return || sale.original_id
            ? sale.return_date
            : sale.sale_date,
        type: "sale",
        sale_id: sale.id,
        orderId: sale.transaction_id || sale.id,
      };

      if (sale.transaction_fees) {
        expenses.push({
          ...expense,
          id: `sale-${sale.id}-transaction_fees`,
          integration_name: sale.sale_platform,
          amount: sale.transaction_fees,
          account: "Transaction Fees",
          sale_property: "transaction_fees",
        });
      }

      if (sale.extra_transaction_fees) {
        expenses.push({
          ...expense,
          id: `sale-${sale.id}-extra_transaction_fees`,
          integration_name: sale.sale_platform,
          amount: sale.extra_transaction_fees,
          account: "Transaction Fees",
          sale_property: "extra_transaction_fees",
        });
      }

      if (sale.purchase_price) {
        expenses.push({
          ...expense,
          id: `sale-${sale.id}-purchase_price`,
          amount: sale.purchase_price,
          account: "Cost of Sold Inventory",
          sale_property: "purchase_price",
        });
      }

      if (sale.shipping_cost) {
        expenses.push({
          ...expense,
          id: `sale-${sale.id}-shipping_cost`,
          integration_name: sale.sale_platform,
          amount: sale.shipping_cost,
          account: "Shipping Fees",
          sale_property: "shipping_cost",
        });
      }

      if (sale.extra_shipping_cost) {
        expenses.push({
          ...expense,
          id: `sale-${sale.id}-extra_shipping_cost`,
          integration_name: sale.sale_platform,
          amount: sale.extra_shipping_cost,
          account: "Shipping Fees",
          sale_property: "extra_shipping_cost",
        });
      }

      if (sale.sales_tax && sale.liable_to_pay) {
        const bankAccount = sale.sale_platform
          ? bankAccounts.find((ba) => ba.name === sale.sale_platform)
          : undefined;
        expenses.push({
          ...expense,
          id: `sale-${sale.id}-extra_shipping_cost`,
          integration_name: sale.sale_platform,
          amount: -sale.sales_tax,
          account: "Sales Tax Payable",
          sale_property: "sales_tax",
          type: "sales-tax-payable",
          bank_account_id: bankAccount?.id,
        });
      }
    }

    return expenses as Transaction[];
  }
);
export const expensesSelector = createSelector(
  [transactionsSelector, expensesOnSalesSelector],
  (items, expensesOnSales) => [...items, ...expensesOnSales]
);
const ExpensesFilterDefinition = {
  date: "date" as const,
  reviewed: "boolean" as const,
  type: "string" as const,
  orderId: "string" as const,
  amount: "number" as const,
  integration_name: "string" as const,
};
export const filteredExpensesSelector = createSelector(
  [
    expensesSelector,
    (_, models) => (models?.length > 0 ? models : undefined),
    effectiveUserTimeZoneSelector,
  ],
  (expenses, models, timeZone) =>
    models
      ? models.reduce(
          (expenses, model) =>
            model
              ? filter(ExpensesFilterDefinition, expenses, model, {
                  dateComparisonTimeZone: timeZone,
                })
              : expenses,
          expenses
        )
      : expenses
);

export const transferTransactionSelector = createSelector(
  [
    transactionsSelector,
    (_, { date }) => date,
    (_, { amount }) => amount,
    (state, { gl_account_id }) =>
      generalLedgerAccountSelector(state, { id: gl_account_id }),
  ],
  (transactions, date, amount, account) => {
    const offset = 14 * 24 * 60 * 60 * 1000; // 24 days
    const fixedAmount = (-1 * amount).toFixed(2);
    for (const transaction of transactions) {
      if (transaction.journal_entry_id) continue;
      if (transaction.items) continue;
      const fixed = (transaction.amount || 0).toFixed(2);
      if (fixedAmount !== fixed) continue;
      if (account && account.name !== transaction.integration_name) continue;
      if (
        !(transaction.date instanceof Date) ||
        Math.abs(transaction.date.getTime() - date.getTime()) > offset
      )
        continue;
      if (transaction.mapped) continue;
      if (transaction.orderId) continue;
      return transaction;
    }
  }
);

const BalanceTransferDayOffsetMs = 7 * 24 * 60 * 60 * 1000;

export const balanceTransferMatchSelector = createSelector(
  [
    transactionsSelector,
    (_, { bank_account_id }) => bank_account_id,
    (_, { date }) => date,
    (_, { amount }) => amount,
  ],
  (transactions, bank_account_id, date, amount) => {
    if (!date || !bank_account_id || amount == null) return undefined;
    const targetTime = date.getTime();
    const fixedAmount = (-1 * amount).toFixed(2);
    let best: Transaction | undefined;
    let bestDiff = Infinity;
    for (const transaction of transactions) {
      if (transaction.journal_entry_id) continue;
      if (transaction.items?.length) continue;
      if (transaction.mapped) continue;
      if (transaction.orderId) continue;
      if (transaction.bank_account_id === bank_account_id) continue;
      if (!(transaction.date instanceof Date)) continue;

      const fixed = (transaction.amount ?? 0).toFixed(2);
      if (fixed !== fixedAmount) continue;

      const diff = Math.abs(transaction.date.getTime() - targetTime);
      if (diff > BalanceTransferDayOffsetMs) continue;
      if (diff < bestDiff) {
        bestDiff = diff;
        best = transaction;
      }
    }
    return best;
  }
);

type Entry =
  | { type: "sale"; item: Sale }
  | { type: "inventory"; item: Inventory };

export const linkedItemsSelector = createSelector(
  [getAllSales, getInventory, (_, { date }: { date?: Date } = {}) => date],
  (sales, inventory, date): Entry[] => {
    const entries: Entry[] = [];
    const dateString = date ? toDateString(date) : undefined;

    for (const sale of sales) {
      if (sale.is_return || sale.original_id) continue;
      if (sale.transaction_inventory_id) continue;
      if (sale.bank_account === "Balance Transfer") continue;
      if (
        dateString &&
        (!sale.purchase_date || dateString !== toDateString(sale.purchase_date))
      )
        continue;
      entries.push({ type: "sale", item: sale });
    }

    for (const item of inventory) {
      if (item.transaction_inventory_id) continue;
      if (item.bank_account === "Balance Transfer") continue;
      if (
        dateString &&
        (!item.purchase_date || dateString !== toDateString(item.purchase_date))
      )
        continue;
      entries.push({ type: "inventory", item });
    }

    return entries;
  }
);

export const linkedItemDocumentsSelector = createSelector(
  [
    getAllSales,
    getInventory,
    (_, items: { type: "sale" | "inventory"; id: string }[]) => items,
  ],
  (sales, inventory, items): Entry[] => {
    const entries: Entry[] = [];
    if (items?.length) {
      for (const sale of sales) {
        for (const { type, id } of items) {
          if (type === "sale" && id === sale.id)
            entries.push({ type, item: sale });
        }
      }

      for (const item of inventory) {
        for (const { type, id } of items) {
          if (type === "inventory" && id === item.id)
            entries.push({ type, item });
        }
      }
    }

    return entries;
  }
);

export const transactionByIdSelector = createSelector(
  [transactionsSelector, (_, id) => id],
  (transactions, id) => {
    if (id) return transactions.find((t) => t.id === id);
  }
);

export const journalEntrySelector = createSelector(
  [transactionsSelector, (_, id) => id],
  (transactions, id) => {
    if (id) {
      const ts = transactions.filter((t) => t.journal_entry_id === id);
      if (ts.length) {
        return {
          id,
          date: ts[0].date,
          rows: ts,
        };
      }
    }
  }
);

/**
 * Aggregate every `Transactions` doc sharing a `split_group_id` into a single
 * split view-model, mirroring `journalEntrySelector`. `base` is the clicked row
 * (when `id` is provided) or the first sibling, and supplies the shared header
 * fields. Returns `undefined` when the group is not present in Redux.
 */
export const splitGroupSelector = createSelector(
  [
    transactionsSelector,
    (_, split_group_id) => split_group_id,
    (_, __, id) => id,
  ],
  (transactions, split_group_id, id) => {
    if (!split_group_id) return undefined;
    const rows = transactions.filter(
      (t) => t.split_group_id === split_group_id
    );
    if (!rows.length) return undefined;
    const base = (id && rows.find((r) => r.id === id)) || rows[0];
    const total = rows.reduce((acc, r) => acc + (r.amount || 0), 0);
    return { split_group_id, base, rows, total };
  }
);

export function mapTransactionToMappedTransaction(
  transaction: TransactionWithDetails
): MappedTransaction | undefined {
  if (!transaction.date) return;

  const transactionType = (transaction as any).type;
  if (transactionType === "sales-tax-payable" || transactionType === "sale") {
    return {
      id: transaction.id,
      collection: "sale",
      collection_id: (transaction as any).sale_id || transaction.id,
      date: transaction.date,
      general_ledger_account: transaction.account,
      bank_account: transaction.integration_name || "",
      description: transaction.description,
      amount: transaction.amount,
      charge_description: transaction.name || "",
    };
  }

  return {
    id: `transaction-${transaction.id}`,
    collection: "transaction",
    collection_id: transaction.id,
    date: transaction.date,
    general_ledger_account: transaction.account,
    bank_account: transaction.integration_name || "",
    description: transaction.description,
    amount: transaction.amount,
    type:
      transaction.accountDoc?.type?.[0] === "income"
        ? "income"
        : transaction.accountDoc?.type?.[0] === "expenses"
        ? "expenses"
        : transaction.accountDoc?.type?.[0] === "assets" &&
          transaction.accountDoc?.type?.[1] === "inventory"
        ? "inventory"
        : undefined,
    journal_entry_id: transaction.journal_entry_id,
    charge_description: transaction.name || "",
    split_group_id: transaction.split_group_id,
  };
}

// Not the best name, tbh
export const transactionsAsTransactionsSelector = createSelector(
  [transactionsSelector],
  (transactions): MappedTransaction[] => {
    const mappedTransactions: MappedTransaction[] = [];
    for (const transaction of transactions) {
      const t = mapTransactionToMappedTransaction(transaction);
      if (!t) continue;
      mappedTransactions.push(t);
    }

    return mappedTransactions;
  }
);

const MappedTransactionFilterDefinition = {
  date: "date" as const,
  general_ledger_account: "string" as const,
  bank_account: "string" as const,
  description: "string" as const,
  amount: "number" as const,
  collection: "string" as const,
  type: "string" as const,
};

export const mappedTransactionSelector = createSelector(
  [
    transactionsAsTransactionsSelector,
    salesAsTransactionsSelector,
    inventoryAsTransactionsSelector,
    (_, models) => (models?.length > 0 ? models : undefined),
    effectiveUserTimeZoneSelector,
  ],
  (transactions, sales, inventory, models, timeZone) => {
    const all = [...transactions, ...sales, ...inventory];
    return models
      ? models.reduce(
          (all, model) =>
            model
              ? filter(MappedTransactionFilterDefinition, all, model, {
                  dateComparisonTimeZone: timeZone,
                })
              : all,
          all
        )
      : all;
  }
);
